<script setup>
// The basket panel's contents. Ported onto the design system's own classes, so this and
// the Blade demo panel (resources/views/partials/demo/basket-panel.blade.php) render the
// same markup: .basket_countdown for the held-time notice, a <ul class="basket_items"> of
// <x-basket-item> twins, and the footer's .summary_table plus its CTA.
//
// No root element of its own. The Blade panel's body has no wrapper either, so the two
// space their children on the same p/ul margins rather than this side alone imposing a
// flex column - which is also why the port produced no arrangement styling to house.
import { computed, inject } from 'vue';
import BasketItem from '@/components/BasketItem.vue';
import BasketVoucher from '@/components/BasketVoucher.vue';
import SvgIcon from '@/components/SvgIcon.vue';
import { formatPrice } from '@/shared/formatPrice.js';
import { useReservationCountdown } from '@/booking/composables/useReservationCountdown.js';

const props = defineProps({
    items:            { type: Array,   default: () => [] },
    vouchers:         { type: Array,   default: () => [] },
    total:            { type: Number,  default: 0 },
    discount:         { type: Number,  default: 0 },
    // The discount code's own saving, and the code it came from. Separate from `discount`
    // so the row can name it, and because the two are different mechanisms.
    discountCode:     { type: Number,  default: 0 },
    discountCodeName: { type: String,  default: null },
    // The health club membership saving. Its own figure again, for the same reason: the
    // customer earned it by proving something, so it is named rather than absorbed.
    memberDiscount:   { type: Number,  default: 0 },
    payable:          { type: Number,  default: 0 },
    error:            { type: String,  default: null },
    // What the last change did as a side effect, as opposed to why it failed - today only
    // a discount code that no longer applies after an item was edited. Shown as its own
    // line rather than through `error`, which would read as though the change was refused.
    notice:           { type: String,  default: null },
    checkoutUrl:      { type: String,  required: true },
    hasExpiry:        { type: Boolean, default: false },
    expiresInSeconds: { type: Number,  default: 0 },
});

const emit = defineEmits(['remove', 'removeVoucher', 'edit']);

// Several root nodes, so Vue's single-root attribute fallthrough does not apply. Nothing
// passes attributes here, but declaring it keeps a future caller's stray class from
// warning instead of landing somewhere arbitrary.
defineOptions({ inheritAttrs: false });

// Getter, not a plain number - this component stays mounted while the basket is refetched.
const { expired: countdownExpired, label } = useReservationCountdown(() => props.expiresInSeconds);

// Only meaningful when something in the basket is actually being held.
const expired = computed(() => props.hasExpiry && countdownExpired.value);

// Times are picked at checkout. Gifts and treatment-free days have none, and a held item already has one.
const choosesTimesAtCheckout = computed(() => props.items.some(item => item.needsScheduling && ! item.locked));

// The subtotal and checkout link belong in the panel's sticky footer, not at the end of
// its scrolling body. Provided by PopOutPanel.vue; null when this is rendered outside a
// panel, in which case the teleport is disabled and they render inline as before.
const panelFooter = inject('popOutPanelFooter', null);
</script>

<template>
    <!-- .form_error is the design system's general-purpose failure-message class
         (includes/form.scss), not a form-specific one. -->
    <p v-if="error" class="form_error">{{ error }}</p>

    <!-- Not an error: the change went through, this is what it cost. Same class for now -
         the design system has no notice-text class, and inventing one for a single message
         would be a component-owned style in the wrong place. -->
    <p v-if="notice" class="form_error">{{ notice }}</p>

    <!-- .icon_text needs the <svg> as its direct first child, which SvgIcon.vue's bare
         root gives it. .basket_countdown itself has no rules yet - the notice renders as
         plain coloured text until its box is designed, which the Blade demo panel says
         belongs in includes/widgets/. -->
    <p v-if="hasExpiry && !expired" class="basket_countdown icon_text green_text"><SvgIcon name="clock" />Your reserved treatment time is held for <strong>{{ label }}</strong>.</p>
    <p v-else-if="expired" class="basket_countdown expired icon_text red_text"><SvgIcon name="clock" />Your reserved treatment time has expired. Don't worry, you will be able to choose a new time at checkout.</p>

    <!-- A voucher with nothing to spend it on yet is not an empty basket, and that is
         exactly what the redeem journey leaves behind on its choose_product outcome. -->
    <p v-if="!items.length && !vouchers.length && !error">Your basket is empty.</p>

    <ul v-if="items.length" class="basket_items">
        <BasketItem
            v-for="item in items"
            :key="item.guid"
            :type="item.type"
            :name="item.name"
            :price="item.price"
            :was-price="item.wasPrice"
            :date="item.date"
            :people="item.people"
            :location-name="item.locationName"
            :editable="item.editable"
            :locked="item.locked"
            :voucher-provider="item.voucherProvider"
            @remove="emit('remove', item.guid)"
            @edit="emit('edit', item.guid)"
        />
    </ul>

    <!-- Vouchers on the order. Below the items, because a voucher only makes sense once
         you know what it is being spent on.

         An h5 because PopOutPanel.vue titles the panel with an h4, so this is the level
         beneath it - h5 is 16px/600 against that h4's 16px/700 (type.scss), so it reads
         as a section label with no type override needed. -->
    <template v-if="vouchers.length">
        <h5 class="basket_vouchers_title">Vouchers</h5>

        <ul class="basket_vouchers">
            <BasketVoucher
                v-for="voucher in vouchers"
                :key="voucher.id"
                :code="voucher.code"
                :provider="voucher.provider"
                :covers="voucher.covers"
                :balance="voucher.balance"
                :previously-used="voucher.previouslyUsed"
                @remove="emit('removeVoucher', voucher.id)"
            />
        </ul>
    </template>

    <Teleport :to="panelFooter" :disabled="!panelFooter">
        <template v-if="items.length">
            <!-- .summary_table (tables.scss), the same class the Blade panel's footer
                 totals and the checkout's Booking Summary use: one visual pattern, one
                 style, wherever the figures appear. -->
            <table class="summary_table">
                <tbody>
                    <tr>
                        <th>Subtotal</th>
                        <td>{{ formatPrice(total) }}</td>
                    </tr>
                    <!-- One combined line for both voucher mechanisms: a line a voucher
                         covers outright, and a monetary voucher's balance spent against
                         eligible spend. Shown only when something is actually coming off,
                         so a basket with no vouchers reads exactly as it did before. -->
                    <tr v-if="discount > 0">
                        <th>Vouchers</th>
                        <td>-{{ formatPrice(discount) }}</td>
                    </tr>
                    <!-- Named where we know the code, so the customer can see WHICH saving
                         this is - the thing V1's quietly-reduced line prices could not. -->
                    <tr v-if="discountCode > 0">
                        <th>{{ discountCodeName ? `Discount (${discountCodeName})` : 'Discount' }}</th>
                        <td>-{{ formatPrice(discountCode) }}</td>
                    </tr>
                    <!-- Only ever present alongside a code that stacks with it: a code that
                         does not forfeits this outright, server side, so the two rows cannot
                         show a saving the customer is not getting. -->
                    <tr v-if="memberDiscount > 0">
                        <th>Member discount</th>
                        <td>-{{ formatPrice(memberDiscount) }}</td>
                    </tr>
                    <tr v-if="discount > 0 || discountCode > 0 || memberDiscount > 0">
                        <th>Total to pay</th>
                        <td>{{ formatPrice(payable) }}</td>
                    </tr>
                </tbody>
            </table>

            <a :href="checkoutUrl" class="button primary major block">Checkout Now</a>

            <p v-if="choosesTimesAtCheckout" class="basket_checkout_note">You'll choose your treatment times on the next page.</p>
        </template>
    </Teleport>
</template>
